import React, { useState, useEffect, useMemo } from 'react';
import { useCortes } from '../../hooks/useCortes';
import { ConfirmModal } from '../common/ConfirmModal';
import { MenuAcciones } from '../common/MenuAcciones';
import { formatCurrency, formatCurrencyInt } from '../../utils/formatters';
import { nombreMes, mesCorto, periodoDesde } from '../../utils/periodos';

// Busca 'AAAA-MM' en las primeras líneas del CSV (metadatos del reporte de
// IBM Cloud, p. ej. "Billing Month","2026-09"). Solo sirve para precargar.
const detectarMes = (texto) => {
  const cabecera = texto.split(/\r?\n/).slice(0, 6).join('\n');
  const m = cabecera.match(/\b(20\d{2})-(0[1-9]|1[0-2])\b/);
  return m ? `${m[1]}-${m[2]}` : '';
};

/**
 * Modal para cargar el CSV de un mes: mes (se intenta detectar del archivo),
 * TRM opcional y el archivo.
 */
const CargarMesModal = ({ onClose, onSubmit, enviando, periodoInicial }) => {
  const [file, setFile] = useState(null);
  const [periodo, setPeriodo] = useState(periodoInicial || periodoDesde(-1));
  const [trm, setTrm] = useState('');
  const [detectado, setDetectado] = useState('');

  const elegirArchivo = (f) => {
    setFile(f || null);
    setDetectado('');
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const p = detectarMes(String(reader.result || ''));
      if (p) {
        setPeriodo(p);
        setDetectado(p);
      }
    };
    reader.readAsText(f.slice(0, 4096));
  };

  const valido = file && /^\d{4}-\d{2}$/.test(periodo) && (trm === '' || parseFloat(trm) > 0);

  return (
    <div className="modal-overlay" onClick={enviando ? undefined : onClose}>
      <div className="modal-content consumo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h3 className="modal-title">Cargar consumo de un mes</h3>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar" disabled={enviando}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-body consumo-form">
          <label className="consumo-campo">
            <span>Archivo CSV de IBM Cloud</span>
            <input type="file" accept=".csv" onChange={(e) => elegirArchivo(e.target.files[0])} />
          </label>
          <label className="consumo-campo">
            <span>Mes del consumo</span>
            <input
              className="proceso-input"
              type="month"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
            />
            {detectado && detectado === periodo && (
              <small className="consumo-ayuda">Detectado en el archivo</small>
            )}
          </label>
          <label className="consumo-campo">
            <span>TRM del mes (USD → COP) · opcional</span>
            <input
              className="proceso-input"
              type="number"
              min="0"
              step="0.01"
              value={trm}
              placeholder="Puedes ponerla después"
              onChange={(e) => setTrm(e.target.value)}
            />
          </label>
        </div>
        <div className="modal-footer">
          <button className="modal-btn modal-btn-ghost" onClick={onClose} disabled={enviando}>Cancelar</button>
          <button
            className="modal-btn modal-btn-primary"
            disabled={!valido || enviando}
            onClick={() => onSubmit({ file, periodo, trm })}
          >
            {enviando ? 'Guardando…' : 'Guardar mes'}
          </button>
        </div>
      </div>
    </div>
  );
};

/** TRM editable en línea; persiste al salir del campo. */
const TrmInput = ({ value, onSave }) => {
  const [val, setVal] = useState(value ? String(value) : '');
  useEffect(() => { setVal(value ? String(value) : ''); }, [value]);
  const commit = () => {
    const n = parseFloat(val);
    if (n > 0 && n !== value) onSave(n);
    else if (!(n > 0)) setVal(value ? String(value) : '');
  };
  return (
    <input
      className="consumo-trm-input"
      type="text"
      inputMode="decimal"
      value={val}
      placeholder="Agregar TRM"
      aria-label="TRM del mes"
      onChange={(e) => setVal(e.target.value.replace(/[^\d.]/g, ''))}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
};

/**
 * Módulo Costos Cloud (2026-10). Guarda el CSV de IBM Cloud de cada mes con
 * su TRM para recorrer el histórico mes a mes. El costo del proveedor vive
 * aparte, en Costos Proveedor.
 */
export const CostosCloudModule = ({ showNotification }) => {
  const {
    cortes, detalle, loadingLista, loadingDetalle,
    loadCortes, loadDetalle, cargarMes, setTrm, eliminar
  } = useCortes(showNotification);

  const [periodo, setPeriodo] = useState('');
  const [modal, setModal] = useState(null); // { periodoInicial }
  const [enviando, setEnviando] = useState(false);
  const [pendiente, setPendiente] = useState(null); // carga a confirmar (mes ya existe)
  const [confirm, setConfirm] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [abiertos, setAbiertos] = useState({});

  // Al entrar, abre el último mes cargado.
  useEffect(() => {
    loadCortes().then((lista) => {
      if (lista.length > 0) setPeriodo((p) => p || lista[lista.length - 1].periodo);
    });
  }, [loadCortes]);

  useEffect(() => {
    loadDetalle(periodo);
    setAbiertos({});
  }, [periodo, loadDetalle]);

  const idx = cortes.findIndex((c) => c.periodo === periodo);
  const corte = detalle?.corte?.periodo === periodo ? detalle.corte : null;
  const trm = corte?.trm || null;

  const enviarCarga = async (datos, reemplazar = false) => {
    setEnviando(true);
    const r = await cargarMes({ ...datos, reemplazar });
    setEnviando(false);
    if (r.existe) {
      setPendiente(datos);
      return;
    }
    if (r.error) {
      showNotification?.('error', 'Consumo mensual', r.error);
      return;
    }
    setModal(null);
    setPendiente(null);
    showNotification?.(
      'success',
      'Consumo mensual',
      `${nombreMes(datos.periodo)} ${r.reemplazado ? 'reemplazado' : 'guardado'}: ${r.filas} líneas, ${formatCurrency(r.total_usd)}`
    );
    await loadCortes();
    if (datos.periodo === periodo) loadDetalle(periodo);
    else setPeriodo(datos.periodo);
  };

  // Consumo agrupado por servicio, con sus recursos adentro.
  const servicios = useMemo(() => {
    if (!detalle || !corte) return [];
    const q = busqueda.trim().toLowerCase();
    const map = new Map();
    detalle.consumo.forEach((l) => {
      if (q && !l.service.toLowerCase().includes(q) && !l.instance.toLowerCase().includes(q)) return;
      if (!map.has(l.service)) map.set(l.service, { service: l.service, total: 0, recursos: [] });
      const s = map.get(l.service);
      s.total += l.costo_usd;
      s.recursos.push(l);
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [detalle, corte, busqueda]);

  const totalUsd = corte?.total_usd || 0;
  const maxHist = Math.max(...cortes.map((c) => c.total_usd), 0);
  const pct = (v) => (totalUsd ? `${((v / totalUsd) * 100).toFixed(2)}%` : '—');
  const cop = (usd) => (trm ? formatCurrencyInt(usd * trm) : '—');

  const accionesMes = corte ? [
    { label: 'Reemplazar CSV de este mes', onClick: () => setModal({ periodoInicial: periodo }) },
    {
      label: 'Eliminar mes',
      danger: true,
      onClick: () => setConfirm({
        title: 'Eliminar mes',
        message: `¿Eliminar ${nombreMes(periodo)}? Se borra el consumo de Cloud guardado para ese mes.`,
        onConfirm: async () => {
          setConfirm(null);
          if (await eliminar(corte.id)) {
            showNotification?.('success', 'Consumo mensual', `${nombreMes(periodo)} eliminado`);
            const lista = await loadCortes();
            setPeriodo(lista.length ? lista[lista.length - 1].periodo : '');
          }
        }
      })
    }
  ] : [];

  return (
    <div className="module-proceso">
      <div className="module-header">
        <h1>Costos Cloud</h1>
        <p>Histórico del consumo de IBM Cloud, mes a mes</p>
      </div>

      {!loadingLista && cortes.length === 0 ? (
        <div className="consumo-vacio">
          <h2>Aún no hay meses guardados</h2>
          <p>Carga el CSV de facturación de IBM Cloud de un mes para empezar el histórico.</p>
          <button className="btn-primary consumo-cargar-btn" onClick={() => setModal({})}>
            Cargar mes
          </button>
        </div>
      ) : (
        <>
          <div className="consumo-toolbar">
            <div className="consumo-nav">
              <button
                type="button"
                className="consumo-nav-btn"
                disabled={idx <= 0}
                onClick={() => setPeriodo(cortes[idx - 1].periodo)}
                aria-label="Mes anterior"
              >
                ‹
              </button>
              <select
                className="consumo-nav-select"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                aria-label="Mes"
              >
                {cortes.map((c) => (
                  <option key={c.periodo} value={c.periodo}>{nombreMes(c.periodo)}</option>
                ))}
              </select>
              <button
                type="button"
                className="consumo-nav-btn"
                disabled={idx === -1 || idx >= cortes.length - 1}
                onClick={() => setPeriodo(cortes[idx + 1].periodo)}
                aria-label="Mes siguiente"
              >
                ›
              </button>
            </div>
            {corte && (
              <span className="consumo-meta">
                {corte.archivo} · cargado por {corte.cargado_por || '—'} el{' '}
                {new Date(corte.updated_at).toLocaleDateString('es-CO')}
              </span>
            )}
            <div className="consumo-toolbar-acciones">
              <button className="btn-primary consumo-cargar-btn" onClick={() => setModal({})}>
                Cargar mes
              </button>
              {corte && <MenuAcciones label="Acciones del mes" acciones={accionesMes} />}
            </div>
          </div>

          {cortes.length > 1 && (
            <section className="consumo-hist" aria-label="Consumo Cloud por mes (USD)">
              <span className="consumo-hist-titulo">Consumo Cloud por mes (USD)</span>
              <div className="consumo-hist-barras">
                {cortes.map((c) => (
                  <button
                    key={c.periodo}
                    type="button"
                    className={`consumo-hist-col ${c.periodo === periodo ? 'activa' : ''}`}
                    onClick={() => setPeriodo(c.periodo)}
                    title={`${nombreMes(c.periodo)}: ${formatCurrency(c.total_usd)}`}
                  >
                    <span className="consumo-hist-valor">{formatCurrencyInt(c.total_usd)}</span>
                    <span className="consumo-hist-pista">
                      <span
                        className="consumo-hist-barra"
                        style={{ height: `${maxHist ? Math.max(2, (c.total_usd / maxHist) * 100) : 0}%` }}
                      />
                    </span>
                    <span className="consumo-hist-mes">{mesCorto(c.periodo)}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {loadingDetalle && !corte && <p className="proceso-hint">Cargando…</p>}

          {corte && (
            <>
              <div className="consumo-kpis">
                <div className="consumo-kpi">
                  <span className="consumo-kpi-label">Cloud (USD)</span>
                  <span className="consumo-kpi-valor">{formatCurrency(totalUsd)}</span>
                  <span className="consumo-kpi-sub">{detalle.consumo.length} recursos · {corte.filas} líneas</span>
                </div>
                <div className="consumo-kpi">
                  <span className="consumo-kpi-label">TRM del mes</span>
                  <TrmInput
                    value={trm}
                    onSave={async (n) => {
                      if (await setTrm(corte.id, n)) {
                        showNotification?.('success', 'TRM', `TRM de ${nombreMes(periodo)}: ${n}`);
                      }
                    }}
                  />
                  <span className="consumo-kpi-sub">{trm ? 'Clic para editar' : 'Sin TRM no hay valores en pesos'}</span>
                </div>
                <div className="consumo-kpi consumo-kpi-total">
                  <span className="consumo-kpi-label">Cloud (COP)</span>
                  <span className="consumo-kpi-valor">{cop(totalUsd)}</span>
                  {!trm && <span className="consumo-kpi-sub">Agrega la TRM para ver el valor en pesos</span>}
                </div>
              </div>

              <div className="consumo-tabs">
                <span className="consumo-tab activa">Detalle por servicio</span>
                <input
                  className="proceso-input consumo-buscar"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar servicio o recurso"
                />
              </div>

              <div className="consumo-tabla-wrap">
                  <table className="consumo-tabla">
                    <thead>
                      <tr>
                        <th>Servicio / recurso</th>
                        <th className="num">USD</th>
                        <th className="num">COP</th>
                        <th className="num">% del mes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {servicios.length === 0 && (
                        <tr><td colSpan={4} className="consumo-tabla-vacia">Nada coincide con la búsqueda</td></tr>
                      )}
                      {servicios.map((s) => {
                        const abierto = abiertos[s.service] || busqueda.trim() !== '';
                        return (
                          <React.Fragment key={s.service}>
                            <tr
                              className="consumo-fila-servicio"
                              onClick={() => setAbiertos((a) => ({ ...a, [s.service]: !a[s.service] }))}
                            >
                              <td>
                                <span className={`consumo-chevron ${abierto ? 'abierto' : ''}`}>›</span>
                                {s.service}
                                <span className="consumo-cuenta">{s.recursos.length}</span>
                              </td>
                              <td className="num">{formatCurrency(s.total)}</td>
                              <td className="num">{cop(s.total)}</td>
                              <td className="num">{pct(s.total)}</td>
                            </tr>
                            {abierto && s.recursos.map((r) => (
                              <tr key={`${s.service}|${r.instance}`} className="consumo-fila-recurso">
                                <td>{r.instance || '(sin nombre)'}</td>
                                <td className="num">{formatCurrency(r.costo_usd)}</td>
                                <td className="num">{cop(r.costo_usd)}</td>
                                <td className="num">{pct(r.costo_usd)}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
            </>
          )}
        </>
      )}

      {modal && (
        <CargarMesModal
          periodoInicial={modal.periodoInicial}
          enviando={enviando}
          onClose={() => setModal(null)}
          onSubmit={(datos) => enviarCarga(datos)}
        />
      )}

      {pendiente && (
        <ConfirmModal
          title="Ese mes ya está cargado"
          message={`${nombreMes(pendiente.periodo)} ya tiene datos. ¿Reemplazarlos con este archivo? Se borra el consumo guardado de ese mes.`}
          confirmLabel="Reemplazar"
          onConfirm={() => enviarCarga(pendiente, true)}
          onClose={() => setPendiente(null)}
        />
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          tone={confirm.tone}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
};
